import { LazyUndoRedoAction, UndoRedoAction } from "@/stores/undoRedoStore";
function getCommentName(comment) {
    if (comment.color !== "none") {
        return `${comment.color} ${comment.type} comment`;
    }
    else {
        return `${comment.type} comment`;
    }
}
class CommentAction extends UndoRedoAction {
    constructor(store, comment) {
        super();
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "comment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        this.comment = structuredClone(comment);
    }
    get commentName() {
        return getCommentName(this.comment);
    }
}
export class AddCommentAction extends CommentAction {
    get name() {
        return `add ${this.commentName}`;
    }
    undo() {
        this.store.deleteComment(this.comment.id);
    }
    redo() {
        this.store.addComments([this.comment]);
    }
}
export class DeleteCommentAction extends CommentAction {
    get name() {
        return `delete ${this.commentName}`;
    }
    run() {
        this.store.deleteComment(this.comment.id);
    }
    undo() {
        this.store.addComments([this.comment]);
    }
}
export class ChangeColorAction extends UndoRedoAction {
    constructor(store, comment, color) {
        super();
        Object.defineProperty(this, "commentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toColor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromColor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        this.commentId = comment.id;
        this.fromColor = comment.color;
        this.toColor = color;
        this.type = comment.type;
    }
    get name() {
        return `change ${this.type} comment color to ${this.toColor}`;
    }
    run() {
        this.store.changeColor(this.commentId, this.toColor);
    }
    undo() {
        this.store.changeColor(this.commentId, this.fromColor);
    }
}
class LazyMutateCommentAction extends LazyUndoRedoAction {
    constructor(comment, key, data, applyDataCallback) {
        super();
        Object.defineProperty(this, "commentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "startData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "endData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "color", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "applyDataCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.commentId = comment.id;
        this.startData = structuredClone(comment[key]);
        this.endData = structuredClone(data);
        this.applyDataCallback = applyDataCallback;
        this.type = comment.type;
        this.color = comment.color;
    }
    queued() {
        this.applyDataCallback(this.commentId, this.endData);
    }
    get commentName() {
        return getCommentName({ type: this.type, color: this.color });
    }
    get name() {
        return `change ${this.commentName}`;
    }
    updateData(data) {
        this.endData = data;
        this.applyDataCallback(this.commentId, this.endData);
    }
    redo() {
        this.applyDataCallback(this.commentId, this.endData);
    }
    undo() {
        this.applyDataCallback(this.commentId, this.startData);
    }
}
export class LazyChangeDataAction extends LazyMutateCommentAction {
    constructor(store, comment, data) {
        const callback = store.changeData;
        super(comment, "data", data, callback);
    }
    get name() {
        if (this.startData.title !== this.endData.title) {
            return `edit title of ${this.commentName}`;
        }
        if (this.startData.text !== this.endData.text) {
            return `edit text of ${this.commentName}`;
        }
        if (this.startData.size !== this.endData.size) {
            return `change text size of ${this.commentName}`;
        }
        if (this.startData.bold !== this.endData.bold) {
            return `toggle bold of ${this.commentName}`;
        }
        if (this.startData.italic !== this.endData.italic) {
            return `toggle italic of ${this.commentName}`;
        }
        return super.name;
    }
}
export class LazyChangePositionAction extends LazyMutateCommentAction {
    constructor(store, comment, position) {
        const callback = store.changePosition;
        super(comment, "position", position, callback);
    }
    get name() {
        return `move ${this.commentName}`;
    }
}
export class LazyChangeSizeAction extends LazyMutateCommentAction {
    constructor(store, comment, size) {
        const callback = store.changeSize;
        super(comment, "size", size, callback);
    }
    get name() {
        return `resize ${this.commentName}`;
    }
}
export class ToggleCommentSelectedAction extends UndoRedoAction {
    constructor(store, comment) {
        super();
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toggleTo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        this.commentId = comment.id;
        this.type = comment.type;
        this.toggleTo = !store.getCommentMultiSelected(this.commentId);
    }
    get name() {
        if (this.toggleTo === true) {
            return `add ${this.type} comment to selection`;
        }
        else {
            return `remove ${this.type} comment from selection`;
        }
    }
    run() {
        this.store.setCommentMultiSelected(this.commentId, this.toggleTo);
    }
    undo() {
        this.store.setCommentMultiSelected(this.commentId, !this.toggleTo);
    }
}
export class RemoveAllFreehandCommentsAction extends UndoRedoAction {
    constructor(store) {
        super();
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "comments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        const freehandComments = store.comments.filter((comment) => comment.type === "freehand");
        this.comments = structuredClone(freehandComments);
    }
    get name() {
        return "remove all freehand comments";
    }
    run() {
        this.store.deleteFreehandComments();
    }
    undo() {
        this.store.addComments(structuredClone(this.comments));
    }
}
